import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { RootState, appActions, fromApp } from "../../store";
import { Program } from "../api";
import { AppQueryStrings, Languages } from "../../enums";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "../../constants";
import { Observable, combineLatest, filter, first, tap } from "rxjs";
import { isDialogHostPath } from "../../utils/dialog-host/dialog-host";
import type { DialogMessage } from "../dialog/dialog.interface";

export type Product = NonNullable<Awaited<ReturnType<typeof shopify.resourcePicker<"product">>>>[number];
export type Collection = NonNullable<Awaited<ReturnType<typeof shopify.resourcePicker<"collection">>>>[number];
type ExtensionActivation = Awaited<ReturnType<typeof shopify.app.extensions>>[number]["activations"][number];
type ThemeBlockActivation = Extract<ExtensionActivation, { status: unknown }>;

const OPENER_TOKEN_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: "root",
})
export class ShopifyAppBridgeService {
  public readonly iframeHost$: Observable<string> = this.store.select(fromApp.selectIframeHost);
  public readonly iframeShop$: Observable<string> = this.store.select(fromApp.selectIframeShop);
  public readonly iframeProgramId$: Observable<string> = this.store.select(fromApp.selectIframeProgramId);

  private reportingWebVitals = false;

  constructor(private readonly store: Store<RootState>) {
    if (!this.isEmbedded) return;

    combineLatest([this.iframeHost$, this.iframeShop$, this.iframeProgramId$])
      .pipe(
        first(),
        filter(([host, shop, programId]) => !!host && !!shop && !!programId),
        tap(([host, shop, programId]) => {
          this.initializeAppBridge(host, programId, shop);
        }),
      )
      .subscribe();
  }

  public async initializeAppBridge(host: string, program: string, iframeShop: string): Promise<void> {
    if (!this.isEmbedded) throw new Error("Shopify App Bridge is not available in this session");

    this.setLanguage();
    this.reportWebVitals();

    const sessionToken = await this.getSessionToken();

    this.store.dispatch(appActions.setAccessToken({ accessToken: sessionToken }));
    this.store.dispatch(appActions.setSideNav({ active: true, hasLogOut: false }));
    this.store.dispatch(appActions.setIframeProgramId({ iframeProgramId: program }));
    this.store.dispatch(appActions.setIframeProgramName({ iframeProgramName: program }));
    this.store.dispatch(appActions.setIframeIntegrationName({ iframeIntegrationName: Program.IntegrationEnum.Shopify }));
    this.store.dispatch(appActions.setIframeShop({ iframeShop }));
    this.store.dispatch(appActions.setIframeHost({ iframeHost: host }));
  }

  /**
   * Built for Shopify 2.1 judges the LCP, CLS and INP App Bridge measures in the admin (75th percentile, 28 days).
   * The same metrics go to PostHog (`web_vital` events) so we can follow them per page; with the `shopify-debug`
   * meta (every environment but production) App Bridge also logs them to the browser console.
   */
  private reportWebVitals(): void {
    if (this.reportingWebVitals || !window.shopify.webVitals?.onReport) return;
    this.reportingWebVitals = true;

    window.shopify.webVitals
      .onReport(({ metrics }) => {
        metrics.forEach(({ id, name, value, country }) => {
          window.posthog?.capture("web_vital", { metric: name, value, metric_id: id, country, path: window.location.pathname });
        });
      })
      .catch(() => (this.reportingWebVitals = false));
  }

  public async getSessionToken(): Promise<string> {
    if (!this.isEmbedded) return null;
    if (isDialogHostPath()) return this.getSessionTokenFromOpener();
    return window.shopify.idToken();
  }

  public get isEmbedded(): boolean {
    return !!window.shopify?.environment.embedded;
  }

  /** Opens a Shopify admin page (e.g. `customers/42`) without leaving the admin when embedded. */
  public openAdminPage(path: string, storeSlug: string): void {
    if (this.isEmbedded) {
      window.open(`shopify://admin/${path}`, "_top");
      return;
    }
    window.open(`https://admin.shopify.com/store/${storeSlug}/${path}`, "_blank");
  }

  /**
   * Handles of the app's extensions in use: theme blocks and embeds on in the published theme (by block filename)
   * and UI extensions placed on some target. Null outside Shopify or when the admin cannot tell.
   */
  public async getActiveExtensionHandles(): Promise<string[] | null> {
    if (!this.isEmbedded) return null;

    try {
      const extensions = await window.shopify.app.extensions();
      return extensions.flatMap(extension => {
        const activations = extension.activations as ExtensionActivation[];
        if (extension.type === "theme_app_extension") {
          return activations
            .filter((block): block is ThemeBlockActivation => "status" in block && block.status === "active")
            .map(block => block.handle);
        }
        return activations.length ? [extension.handle] : [];
      });
    } catch {
      return null;
    }
  }

  public selectProducts(initialSelectionIds: string[]): Promise<Product[] | null> {
    return this.selectResources("product", initialSelectionIds);
  }

  public selectCollections(initialSelectionIds: string[]): Promise<Collection[] | null> {
    return this.selectResources("collection", initialSelectionIds);
  }

  private async selectResources<ResourceType extends "product" | "collection">(type: ResourceType, initialSelectionIds: string[]) {
    if (!this.isEmbedded) return null;

    const selection = await window.shopify.resourcePicker({
      type,
      action: "select",
      multiple: true,
      selectionIds: initialSelectionIds.map(id => ({ id })),
      ...(type === "product" ? { filter: { variants: false } } : {}),
    });

    return selection ?? null;
  }

  /** The Shopify admin language when the admin offers it, otherwise the default one. */
  public resolveLanguage(): Languages {
    // Inside a Shopify modal App Bridge has no config: the page that opened it passes the locale in the URL
    const locale = new URLSearchParams(window.location.search).get("locale") ?? window.shopify?.config?.locale;
    const userLang = locale?.split("-")[0] as Languages;
    return AVAILABLE_LANGUAGES.includes(userLang) ? userLang : DEFAULT_LANGUAGE;
  }

  /** Inside a Shopify modal App Bridge only offers its environment API: the page that opened it (DialogService) answers. */
  private getSessionTokenFromOpener(): Promise<string> {
    const origin = window.location.origin;
    const dialogId = new URLSearchParams(window.location.search).get(AppQueryStrings.dialogId);
    const requestId = crypto.randomUUID();

    return new Promise<string>((resolve, reject) => {
      if (!window.opener) {
        reject(new Error("The page that opened this Shopify modal is not available"));
        return;
      }

      // Without an answer (the page behind closed its dialog) the modal and its requests must not wait forever
      const timeout = setTimeout(() => {
        window.removeEventListener("message", onMessage);
        reject(new Error("The page that opened this Shopify modal did not answer"));
      }, OPENER_TOKEN_TIMEOUT_MS);

      const onMessage = ({ origin: messageOrigin, data }: MessageEvent<DialogMessage>): void => {
        if (messageOrigin !== origin || data?.type !== "dialog-session-token" || data.requestId !== requestId) return;
        clearTimeout(timeout);
        window.removeEventListener("message", onMessage);
        resolve(data.token);
      };
      window.addEventListener("message", onMessage);
      window.opener.postMessage({ type: "dialog-session-token-request", dialogId, requestId } satisfies DialogMessage, origin);
    });
  }

  private setLanguage(): void {
    this.store.dispatch(appActions.changeLanguage({ language: this.resolveLanguage() }));
  }
}
